/**
 * CallTrackingMetrics HTTP Basic Auth Header Builder (Story 22.3, AC #2, AC #5)
 *
 * **NEW for CTM — no analogue in prior API-key adapters.** CTM is the
 * FIRST adapter in our library to require TWO secrets in one credential
 * bundle, combined via HTTP Basic auth (RFC 7617).
 *
 * Per the [official CTM API users guide on GitHub](https://github.com/calltracking/calltracking.github.io/blob/master/api_users_guide.md)
 * verified 2026-05-15 — *"For curl, you can use `-u ${ACCESS_KEY}:${SECRET_KEY}`"* — the
 * adapter constructs `Authorization: Basic <base64(ACCESS_KEY:SECRET_KEY)>`
 * on every request. This is genuinely the documented format:
 *  - Ahrefs uses `Authorization: Bearer <KEY>` (single token).
 *  - Semrush uses `?key=<KEY>` query parameter (single token).
 *  - Moz uses `x-moz-token: <KEY>` custom header (single token).
 *  - CallRail uses `Authorization: Token token="<KEY>"` custom header
 *    (single token).
 *  - **CTM uses `Authorization: Basic <base64(ACCESS_KEY:SECRET_KEY)>`
 *    standard HTTP Basic — TWO secrets in one credential bundle.**
 *
 * **Header-injection defense (CRLF + null-byte attacks):** the builder
 * rejects inputs containing `\n`, `\r`, or null bytes synchronously
 * because those characters can split a single header value into multiple
 * headers — a classic header-injection vector. RFC 7230 §3.2.4 forbids
 * obsolete line folding in header values, but a malicious customer
 * pasting a CRLF-laden secret into the connect dialog could otherwise
 * inject arbitrary headers downstream. We refuse synchronously at the
 * helper level so the bad input never reaches `fetch`.
 *
 * **Base64 encoding semantics:** native `btoa(rawString)` is used. This
 * is safe for the CTM key alphabet (alphanumeric per the Postman
 * collection observed 2026-05-15) but NOT safe for arbitrary Unicode.
 * The empty-secret + null-byte + CRLF rejection above is the actual
 * input-shape gate; non-ASCII secrets are rejected by the explicit
 * `assertSecretIsSafeAscii` check.
 */

/**
 * Build the `Authorization: Basic <base64(ACCESS_KEY:SECRET_KEY)>`
 * header value.
 *
 * **Critical formatting:** the literal is `Basic <base64-payload>` —
 * with a single space between "Basic" and the Base64-encoded payload.
 * The payload is the literal `ACCESS_KEY:SECRET_KEY` (colon-delimited)
 * encoded via `btoa`. Per RFC 7617 the colon is the delimiter and is
 * forbidden in the userid; we enforce that here defensively.
 *
 * @param accessKey - Plaintext CTM Access Key (one of two secrets).
 * @param secretKey - Plaintext CTM Secret Key (one of two secrets).
 * @returns The full `Basic <base64>` value suitable for the
 *   `Authorization` header.
 * @throws `TypeError` if either input is empty, contains CRLF / null
 *   bytes, contains a literal colon (RFC 7617 §2 userid restriction),
 *   or contains non-ASCII characters.
 */
export function buildCtmBasicAuthHeader(
  accessKey: string,
  secretKey: string,
): string {
  assertSecretIsSafeAscii(accessKey, "accessKey");
  assertSecretIsSafeAscii(secretKey, "secretKey");
  // RFC 7617 §2: the userid MUST NOT contain a colon (it's the
  // delimiter). Defense in depth — even if a customer somehow generated
  // a CTM key containing a colon (the wire format observed on Postman
  // and PHP SDK uses alphanumerics only), the Basic-auth parser on
  // CTM's side would split on the first colon and authenticate against
  // the wrong substring.
  if (accessKey.includes(":")) {
    throw new TypeError(
      "buildCtmBasicAuthHeader: accessKey contains a colon, which is forbidden in HTTP Basic userids (RFC 7617 §2).",
    );
  }
  const payload = `${accessKey}:${secretKey}`;
  // `btoa` is available globally in Cloudflare Workers + Node 18+. The
  // assertSecretIsSafeAscii check above ensures the payload is
  // ASCII-only, so `btoa` cannot throw "invalid character".
  const encoded = btoa(payload);
  return `Basic ${encoded}`;
}

/**
 * Synchronous guard against pathological secret inputs.
 *
 * Rejects:
 *  - Empty strings (a customer pasting an empty input).
 *  - `\n`, `\r`, null-byte: header-injection vectors.
 *  - Non-ASCII characters: `btoa` throws on these AND CTM's wire format
 *    is documented as alphanumeric anyway.
 *  - Leading / trailing whitespace: would change the Base64 payload
 *    and confuse the customer who pasted a key with stray newline.
 *
 * The error message includes the named role (`"accessKey"` /
 * `"secretKey"`) but NEVER the secret value itself — defensive against
 * a future caller forwarding the error message to a log line.
 *
 * @param value - The secret to validate.
 * @param name - The role label (`"accessKey"` or `"secretKey"`) for
 *   error messages.
 * @throws `TypeError` on any invalid shape.
 */
function assertSecretIsSafeAscii(value: string, name: string): void {
  if (typeof value !== "string" || value.length === 0) {
    throw new TypeError(
      `buildCtmBasicAuthHeader: ${name} must be a non-empty string.`,
    );
  }
  if (value !== value.trim()) {
    throw new TypeError(
      `buildCtmBasicAuthHeader: ${name} must not have leading or trailing whitespace.`,
    );
  }
  // Defense against header-injection: CRLF and null bytes split header
  // values into multiple headers on most HTTP libraries.
  if (/[\r\n\0]/.test(value)) {
    throw new TypeError(
      `buildCtmBasicAuthHeader: ${name} must not contain CR, LF, or null bytes (header-injection defense).`,
    );
  }
  // ASCII-only: per the CTM Postman collection observed 2026-05-15,
  // keys are 30-50 character alphanumeric strings. Reject non-ASCII
  // both for `btoa` safety and as defense against pathological inputs.
  // Bounded character class — no catastrophic backtracking on
  // adversarial inputs because the regex is anchored.
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code > 0x7e) {
      throw new TypeError(
        `buildCtmBasicAuthHeader: ${name} must contain only printable ASCII characters.`,
      );
    }
  }
}
